import type { IStudio } from "../../../screens/Dashboard/SubComponents/Studio/interfaces/Studio.interface";

import type { IStudioArea } from "../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Areas.interface";

/**
 * Detecta si un cambio de modelo se limita al LAYOUT de áreas que ya existían: posición/tamaño
 * (`bounds`), encuadre de la fuente (`media.bounds`), filtros (`media.filters`) y estado del editor
 * (`editorState`, p.ej. oculta/bloqueada). Para ese caso `RenderFactory` no necesita tirar y
 * reconstruir la escena entera: `renderAreas` corre cada frame con una firma por área y re-arma solo
 * el sprite/container del área que cambió. Cualquier otra diferencia (áreas agregadas/quitadas o
 * reordenadas, otra fuente, widgets, elementos, alertas, overlays, paneles, máscara, fondo) exige el
 * rebuild completo de siempre.
 */

/** Campos de un área que renderAreas re-aplica solo por firma. */
const AREA_LAYOUT_KEYS = new Set(["bounds", "editorState"]);

/** Campos de `area.media` que renderAreas re-aplica solo por firma. */
const MEDIA_LAYOUT_KEYS = new Set(["bounds", "filters"]);

function withoutKeys(
  value: Record<string, unknown>,
  keys: Set<string>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(value).filter(([key]) => !keys.has(key)),
  );
}

/** Identidad de un área: todo lo que NO es layout (id, label, fuente, controles, estilos, ...). */
function areaIdentity(area: IStudioArea): string {
  const rest = withoutKeys(
    area as unknown as Record<string, unknown>,
    AREA_LAYOUT_KEYS,
  );

  const media =
    area.media && typeof area.media === "object"
      ? withoutKeys(area.media as unknown as Record<string, unknown>, MEDIA_LAYOUT_KEYS)
      : area.media;

  return JSON.stringify({ ...rest, media });
}

/** El modelo sin sus áreas: debe ser idéntico para que el cambio califique como solo-layout. */
function modelWithoutAreas(model: IStudio): string {
  return JSON.stringify(
    withoutKeys(model as unknown as Record<string, unknown>, new Set(["areas"])),
  );
}

export function isAreaLayoutOnlyChange(
  prev: IStudio | null | undefined,
  next: IStudio | null | undefined,
): boolean {
  if (!prev || !next) return false;

  const prevAreas = prev.areas ?? [];

  const nextAreas = next.areas ?? [];

  if (prevAreas.length !== nextAreas.length) return false;

  try {
    if (modelWithoutAreas(prev) !== modelWithoutAreas(next)) return false;

    for (let i = 0; i < prevAreas.length; i++) {
      const a = prevAreas[i];

      const b = nextAreas[i];

      if (!a || !b || a.id !== b.id) return false;

      if (areaIdentity(a) !== areaIdentity(b)) return false;
    }
  } catch {
    // Modelo no serializable: mejor el rebuild completo que asumir.
    return false;
  }

  return true;
}
